import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Wifi, WifiOff, Clock, CheckCircle, AlertCircle, Settings } from 'lucide-react';
import { offlineQueue } from '../services/offlineQueue';
import AppSettings from './AppSettings';

const StatusContainer = styled.div<{ $isOnline: boolean; $hasPendingActions: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid;
  
  /* Mobile responsive positioning */
  @media (max-width: 768px) {
    top: 75px; /* Below mobile header (60px) with some spacing */
    right: 16px;
    font-size: 12px;
    padding: 6px 10px;
    z-index: 1001; /* Above mobile header but below mobile menu */
  }
  
  @media (max-width: 480px) {
    top: 75px; /* Keep consistent with tablet positioning */
    right: 12px;
    font-size: 11px;
    padding: 5px 8px;
  }
  
  ${props => props.$isOnline 
    ? `
      background: rgba(34, 197, 94, 0.1);
      border-color: rgba(34, 197, 94, 0.3);
      color: #16a34a;
    ` 
    : `
      background: rgba(239, 68, 68, 0.1);
      border-color: rgba(239, 68, 68, 0.3);
      color: #dc2626;
    `
  }
  
  ${props => props.$hasPendingActions && !props.$isOnline && `
    background: rgba(245, 158, 11, 0.1);
    border-color: rgba(245, 158, 11, 0.3);
    color: #d97706;
  `}
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
`;

const StatusText = styled.span`
  white-space: nowrap;
`;

const PendingCount = styled.span`
  background: rgba(0, 0, 0, 0.1);
  padding: 2px 6px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
`;

const SettingsButton = styled.button`
  background: transparent;
  border: none;
  color: inherit;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: rgba(0, 0, 0, 0.1);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

interface OnlineStatusIndicatorProps {
  className?: string;
}

export const OnlineStatusIndicator: React.FC<OnlineStatusIndicatorProps> = ({ className }) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingActions, setPendingActions] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showAppSettings, setShowAppSettings] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      console.log('[STATUS] Device came online');
    };

    const handleOffline = () => {
      setIsOnline(false);
      console.log('[STATUS] Device went offline');
    };

    // Listen for online/offline events
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Check pending actions periodically
    const checkPendingActions = async () => {
      try {
        const queue = await offlineQueue.getQueue();
        const pending = queue.filter(action => action.status === 'pending' || action.status === 'failed');
        setPendingActions(pending.length);
        
        // Check if queue is being processed
        const processing = queue.filter(action => action.status === 'processing');
        setIsProcessing(processing.length > 0);
      } catch (error) {
        console.error('[STATUS] Error checking pending actions:', error);
      }
    };

    // Check immediately and then every 5 seconds
    checkPendingActions();
    const interval = setInterval(checkPendingActions, 5000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(interval);
    };
  }, []);

  const getStatusIcon = () => {
    if (isProcessing) {
      return <Clock size={16} className="animate-spin" />;
    }
    
    if (isOnline) {
      return pendingActions > 0 ? <CheckCircle size={16} /> : <Wifi size={16} />;
    }
    
    return pendingActions > 0 ? <AlertCircle size={16} /> : <WifiOff size={16} />;
  };

  const getStatusText = () => {
    if (isProcessing) {
      return 'Syncing...';
    }
    
    if (isOnline) {
      return pendingActions > 0 ? 'Synced' : 'Online';
    }
    
    return pendingActions > 0 ? 'Offline (Pending)' : 'Offline';
  };

  return (
    <>
      <StatusContainer 
        $isOnline={isOnline} 
        $hasPendingActions={pendingActions > 0}
        className={className}
        title={
          isOnline 
            ? pendingActions > 0 
              ? `${pendingActions} actions synced` 
              : 'Connected to server'
            : pendingActions > 0
              ? `${pendingActions} actions pending sync`
              : 'No internet connection'
        }
      >
      <StatusIcon>
        {getStatusIcon()}
      </StatusIcon>
      <StatusText>
        {getStatusText()}
      </StatusText>
      {pendingActions > 0 && (
        <PendingCount>
          {pendingActions}
        </PendingCount>
      )}
      <SettingsButton onClick={() => setShowAppSettings(true)} title="App Settings">
        <Settings size={16} />
      </SettingsButton>
    </StatusContainer>
    
    <AppSettings 
      isOpen={showAppSettings} 
      onClose={() => setShowAppSettings(false)} 
    />
  </>
  );
};

export default OnlineStatusIndicator;
